import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import DestructorTree from "../DestructorTree";
import * as Text from "../state/Text";
import * as Number from "../state/Number";
import { iReference } from "../state/Reference";
import * as Rule from "../state/Rule";
import { iState, iStategroup } from "../state/Stategroup";
import * as Collection from "../state/Collection";
import {
	registerText,
	registerNumber,
	registerFile,
	registerGroup,
	registerStategroup,
	registerCollection,
	registrationDone,
} from "../state/registerClientState";
import {
	createExpressionBuilder,
	DefaultExpressionBuilder,
	Scalar,
	ScalarConsumer,
	ScalarProvider,
	scalarValue,
	getSourceOriginFromStacktrace,
} from "../../../../../stream";
import { Pending, Unknown, Failed, Result, isResolved } from "../Status";
import { FileToken } from "./expressions";

export type CsProperty =
	| api_cs.Ctext__type__property
	| api_cs.Cnumber__type__property
	| api_cs.Cfile__type__property
	| api_cs.Cgroup__type__property
	| api_cs.Cstate_group__type__property
	| api_cs.Ccollection;
export type AppProperty =
	| api_app.Ctext__type__property
	| api_app.Cnumber__type__property
	| api_app.Cfile
	| api_app.Cgroup__type__property
	| api_app.Cstate_group
	| api_app.Ccollection__type__property;

export class Sync<InType, OutType> extends ScalarProvider<Result<OutType>> implements ScalarConsumer<Result<InType>> {
	private _subscriber: ScalarConsumer<Result<OutType>> | undefined;
	private _value: Scalar<Result<OutType>> | undefined;
	private _destructor: DestructorTree | undefined;

	constructor(
		readonly source: Scalar<Result<InType>>,
		private readonly fn: ($: Result<InType>, destructor: DestructorTree) => Scalar<Result<OutType>>,
		readonly __origin?: string
	) {
		super();
	}

	get value() {
		if (this._subscriber === undefined) {
			const value = scalarValue(this.source);
			if (isResolved(value)) {
				const destructor = new DestructorTree();
				const result = this.fn(scalarValue(this.source), destructor);
				destructor.destroy();
				return scalarValue(result);
			} else {
				return value;
			}
		} else {
			return scalarValue(this._value!);
		}
	}

	update(value: Result<InType>) {
		this._destructor?.destroy();
		this._destructor = new DestructorTree();
		const new_value = isResolved(value) ? this.fn(value, this._destructor) : value;
		if (this._value instanceof ScalarProvider) {
			this._value.unsubscribe(this._subscriber!);
		}
		this._value = new_value;
		if (this._value instanceof ScalarProvider) {
			this._value.subscribe(this._subscriber!);
		} else {
			this._subscriber!.update(this._value);
		}
	}

	subscribe(subscriber: ScalarConsumer<OutType>): typeof subscriber {
		if (this._subscriber !== undefined) throw new Error("unexpected");
		this._subscriber = subscriber;
		if (this.source instanceof ScalarProvider) {
			this.source.subscribe(this);
		} else {
			this.update(this.source);
		}
		return subscriber;
	}

	unsubscribe(subscriber: ScalarConsumer<OutType>) {
		if ((<any>global)?.alan_strict && this._subscriber !== subscriber) throw new Error("unexpected");
		this._subscriber = undefined;
		if (this.source instanceof ScalarProvider) {
			this.source.unsubscribe(this);
		}
		if (this._value instanceof ScalarProvider) {
			this._value.unsubscribe(subscriber);
		}
		this._value = undefined;
		this._destructor?.destroy();
	}
}

export function sync<X, Y>(fn: (x: X, destructor: DestructorTree) => Scalar<Y>) {
	const origin = (<any>global)?.alan_debug ? getSourceOriginFromStacktrace(new Error(), 2) : undefined;
	const builder = createExpressionBuilder<Result<X>>();
	(builder as any).steps.push((source: Scalar<X>) => new Sync(source, fn, origin));
	return builder as DefaultExpressionBuilder<Result<X>, Result<Y>>;
}

export function property(
	property_type: api_app.Ctext__type__property
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Ctext__type__property>>;
export function property(
	property_type: api_app.Cnumber__type__property
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cnumber__type__property>>;
export function property(
	property_type: api_app.Cfile
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cfile__type__property>>;
export function property(
	property_type: api_app.Cgroup__type__property
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cgroup__type__property>>;
export function property(
	property_type: api_app.Cstate_group
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cstate_group__type__property>>;
export function property(
	property_type: api_app.Ccollection__type__property
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Ccollection>>;
export function property(
	property_type: AppProperty
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<CsProperty>>;
export function property(
	property_type: AppProperty
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<CsProperty>> {
	const $type = property_type.parent.properties.type.state;
	switch ($type.name) {
		case "text":
			return sync((node: api_cs.Cnode, destructor) =>
				node.destroyed ? Unknown : registerText($type.node, node, destructor)
			);
		case "number":
			return sync((node: api_cs.Cnode, destructor) =>
				node.destroyed ? Unknown : registerNumber($type.node, node, destructor)
			);
		case "file":
			return sync((node: api_cs.Cnode, destructor) =>
				node.destroyed ? Unknown : registerFile($type.node, node, destructor)
			);
		case "group":
			return sync((node: api_cs.Cnode, destructor) =>
				node.destroyed ? Unknown : registerGroup($type.node, node, destructor)
			);
		case "state group":
			return sync((node: api_cs.Cnode, destructor) =>
				node.destroyed ? Unknown : registerStategroup($type.node, node, destructor)
			);
		case "collection":
			return sync((node: api_cs.Cnode, destructor) =>
				node.destroyed ? Unknown : registerCollection($type.node, node, destructor)
			);
	}
}

export function groupNode(
	group_type: api_app.Cgroup__type__property
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cnode>> {
	return property(group_type).pipe(sync((group) => group.properties.node));
}

export function state(
	def: api_app.Cstates__state_group
): DefaultExpressionBuilder<Result<api_cs.Cstate_group__type__property>, Result<api_cs.Cnode>> {
	return sync((stategroup) => {
		const state = stategroup.properties.states.get(def.key_value)!;
		return registrationDone(stategroup, () => iState(state).is_selected)();
	});
}

export function selectedState(
	def: api_app.Cstate_group
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cstates__state_group__type__property>> {
	return property(def).pipe(
		sync((stategroup) => registrationDone(stategroup, () => iStategroup(stategroup).selected_state)())
	);
}

export function parent(): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cnode>> {
	return sync((node: api_cs.Cnode) => node.input.position().input.parent().cast("node"));
}

export function reference(
	def: api_app.Cyes__has_reference
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cnode>> {
	return property(def.parent).pipe(sync((text) => iReference(text.properties.navigable.cast("yes")).referencedNode));
}

export function textValue(
	def: api_app.Ctext__type__property
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<string>> {
	return property(def).pipe(sync((text) => registrationDone(text, () => Text.value(text))()));
}

export function numberValue(
	def: api_app.Cnumber__type__property
): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<number>> {
	return property(def).pipe(sync((number) => registrationDone(number, () => Number.value(number))()));
}

export function fileValue(def: api_app.Cfile): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<FileToken>> {
	return property(def).pipe(
		sync((file) =>
			registrationDone(file, () => {
				switch (file.properties.is_set.state.name) {
					case "no": {
						return Failed;
					}
					case "yes": {
						const yes_is_set = file.properties.is_set.state.node;
						return {
							token: yes_is_set.properties.token,
							extension: yes_is_set.properties.extension,
						};
					}
				}
			})()
		)
	);
}

export function rule(def: api_app.Crules): DefaultExpressionBuilder<Result<api_cs.Cnode>, Result<api_cs.Cnode>> {
	const $definer = def.parent.input.definer().variant;
	switch ($definer.name) {
		case "text":
			return property($definer.definition.parent).pipe(
				sync((text) => Rule.node(text.properties.navigable.cast("yes").properties.rules.properties.rules.get(def.key)!))
			);
		case "state":
			return sync((node: api_cs.Cnode) =>
				Rule.node(node.input.position().cast("state").properties.rules.properties.rules.get(def.key)!)
			);
	}
}

export function entry(
	key: Scalar<Result<string>>
): DefaultExpressionBuilder<Result<api_cs.Ccollection>, Result<api_cs.Cnode>> {
	return createExpressionBuilder<Result<api_cs.Ccollection>>()
		.and(key)
		.pipe(([collection, key]) => {
			if (collection === Failed || key === Failed) {
				return Failed;
			} else if (collection === Unknown || key === Unknown) {
				return Unknown;
			} else if (collection === Pending || key === Pending) {
				return Pending;
			} else {
				return [collection, key] as Result<[api_cs.Ccollection, string]>;
			}
		})
		.pipe(
			sync(([collection, key]: [api_cs.Ccollection, string], destructor) =>
				Collection.openEntry(collection, key, destructor).pipe(sync((entry) => entry.properties.node))()
			)
		);
}
